import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { totpSchema, type TotpFormData } from '@/lib/validation';
import { authApi, ApiError } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { invalidateSessionSensitiveRequests } from '@/lib/sessionSensitiveRequestManager';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    buildCloverOAuthLaunchPath,
    parseCloverOAuthLoginContext,
} from '@/lib/clover/oauth-launch-context';
import {
    buildPasswordChangePath,
    hasNativeAppLoginContinuation,
    NATIVE_APP_LOGIN_PATH,
} from '@/lib/native-app-login-continuation';
import {
    MfaChallengeCard,
    MfaChallengeLoadingCard,
} from '@/components/auth/MfaChallengeCard';
import {
    MfaMethod,
    MfaSetupStatusResponseSchema,
    SessionUserProfileDetailedResponseSchema,
    SetupTotpResponse,
    VerifyMfaLoginResponseSchema,
} from '@anchorbase/pillar-sdk';

function parseQueryMethods(value: string | null): MfaMethod[] {
    if (value === null || value.trim().length === 0) {
        return [];
    }
    return value
        .split(',')
        .map((method) => method.trim())
        .filter((method): method is MfaMethod =>
            method === 'TOTP' || method === 'EMAIL' || method === 'SMS'
        );
}

export default function LoginMfaSetupPage() {
    const log = createComponentLogger('LoginMfaSetupPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const setUserForEffectiveSessionGeneration = useAuthStore(
        (state) => state.setUserForEffectiveSessionGeneration
    );
    const getEffectiveSessionGeneration = useAuthStore(
        (state) => state.getEffectiveSessionGeneration
    );
    const requiredOrganizationName: string | null = searchParams.get(
        'required_organization_name'
    );
    const paymentRequestUuid: string = searchParams.get('payment_request_uuid')?.trim() ?? '';
    const returnToPayment: boolean = searchParams.get('return_to_payment') === '1';
    const cloverOAuthLaunchContext = parseCloverOAuthLoginContext(searchParams);
    const nativeAppLoginContinuation =
        hasNativeAppLoginContinuation(searchParams) &&
        cloverOAuthLaunchContext === null &&
        !returnToPayment;
    const postLoginPath: string =
        cloverOAuthLaunchContext === null
            ? returnToPayment
                ? '/pay'
                : nativeAppLoginContinuation
                  ? NATIVE_APP_LOGIN_PATH
                  : '/dashboard'
            : buildCloverOAuthLaunchPath(cloverOAuthLaunchContext);
    const queryMethods: MfaMethod[] = useMemo(
        () => parseQueryMethods(searchParams.get('mfa_setup_methods')),
        [searchParams]
    );
    const [selectedMethod, setSelectedMethod] = useState<MfaMethod>('EMAIL');
    const [totpSetup, setTotpSetup] = useState<SetupTotpResponse | null>(null);
    const [message, setMessage] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<TotpFormData>({
        resolver: zodResolver(totpSchema),
    });

    const statusQuery = useQuery({
        queryKey: ['loginMfaSetupStatus'],
        queryFn: async () => {
            const response = await authApi.getMfaSetupStatus();
            return MfaSetupStatusResponseSchema.parse(response);
        },
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });

    const availableMethods: MfaMethod[] = useMemo(() => {
        const statusMethods = statusQuery.data?.available_mfa_setup_methods ?? [];
        const methods = statusMethods.length > 0 ? statusMethods : queryMethods;
        return methods.length > 0 ? methods : ['TOTP'];
    }, [queryMethods, statusQuery.data?.available_mfa_setup_methods]);

    useEffect(() => {
        if (!availableMethods.includes(selectedMethod)) {
            setSelectedMethod(availableMethods[0] ?? 'TOTP');
        }
    }, [availableMethods, selectedMethod]);

    const completeLogin = async () => {
        setIsRedirecting(true);
        queryClient.clear();
        useAuthStore.getState().logout();
        invalidateSessionSensitiveRequests();
        try {
            const profileResponse = await sessionSensitiveAuthApi.getProfile();
            if (profileResponse) {
                const profileData =
                    SessionUserProfileDetailedResponseSchema.parse(profileResponse);
                setUserForEffectiveSessionGeneration(
                    profileData,
                    getEffectiveSessionGeneration()
                );
                window.location.assign(
                    profileData.requires_password_change
                        ? buildPasswordChangePath(nativeAppLoginContinuation)
                        : postLoginPath
                );
                return;
            }
            window.location.assign(postLoginPath);
        } catch (err) {
            log.error('Failed to fetch profile after MFA setup', err);
            window.location.assign(postLoginPath);
        }
    };

    const returnToSignIn = () => {
        const nextSearchParams = new URLSearchParams();
        if (paymentRequestUuid.length > 0) {
            nextSearchParams.set('payment_request_uuid', paymentRequestUuid);
        }
        if (returnToPayment) {
            nextSearchParams.set('return_to_payment', '1');
        }
        const nextLoginPath =
            nextSearchParams.toString().length > 0
                ? `/login?${nextSearchParams.toString()}`
                : '/login';

        setIsRedirecting(true);
        runInBackground(
            authApi
                .logout()
                .catch((err) => {
                    log.error('Error clearing expired MFA setup session', err);
                })
                .then(() => {
                    queryClient.clear();
                    useAuthStore.getState().logout();
                    invalidateSessionSensitiveRequests();
                    return navigate(nextLoginPath, { replace: true });
                }),
            (err) => {
                setIsRedirecting(false);
                setError('Could not return to sign in. Please try again.');
                log.error('Error navigating to login', err);
            }
        );
    };

    const requestCodeMutation = useMutation({
        mutationFn: async (method: Exclude<MfaMethod, 'TOTP'>) => {
            if (method === 'EMAIL') {
                return await authApi.requestLoginMfaEmailSetup();
            }
            return await authApi.requestLoginMfaSmsSetup();
        },
        onSuccess: (response) => {
            setError('');
            setMessage(response.message);
        },
        onError: (err: Error) => {
            setMessage('');
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
            log.error('Error requesting MFA setup code', err);
        },
    });

    const setupTotpMutation = useMutation({
        mutationFn: async () => await authApi.setupLoginMfaTotp(),
        onSuccess: (response) => {
            setError('');
            setMessage(response.message);
            setTotpSetup(response);
        },
        onError: (err: Error) => {
            setMessage('');
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
            log.error('Error starting authenticator app setup', err);
        },
    });

    const verifyMutation = useMutation({
        mutationFn: async (data: TotpFormData) => {
            if (selectedMethod === 'EMAIL') {
                const response = await authApi.verifyLoginMfaEmailSetup({
                    code: data.totp_code,
                });
                return VerifyMfaLoginResponseSchema.parse(response);
            }
            if (selectedMethod === 'SMS') {
                const response = await authApi.verifyLoginMfaSmsSetup({
                    code: data.totp_code,
                });
                return VerifyMfaLoginResponseSchema.parse(response);
            }
            if (totpSetup === null) {
                throw new Error('Authenticator app setup has not been started.');
            }
            const response = await authApi.verifyLoginMfaTotpSetup({
                secret: totpSetup.secret,
                code: data.totp_code,
            });
            return VerifyMfaLoginResponseSchema.parse(response);
        },
        onSuccess: () => {
            runInBackground(completeLogin(), (err) => {
                setIsRedirecting(false);
                setError('MFA setup succeeded, but sign-in completion failed.');
                log.error('Error completing MFA setup login', err);
            });
        },
        onError: (err: Error) => {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
            log.error('Error verifying MFA setup', err);
        },
    });

    const onSubmit = (data: TotpFormData) => {
        setError('');
        setMessage('');
        verifyMutation.mutate(data);
    };

    const selectedMethodNeedsDelivery = selectedMethod === 'EMAIL' || selectedMethod === 'SMS';
    const setupContextMessage =
        requiredOrganizationName === null
            ? 'MFA is required before you can continue.'
            : `${requiredOrganizationName} requires MFA before you can continue.`;

    if (isRedirecting) {
        return <MfaChallengeLoadingCard message="Signing you in..." />;
    }

    return (
        <>
            {statusQuery.isError ? (
                <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
                    <div className="w-full max-w-md space-y-4 rounded-lg border border-slate-200 bg-white p-6 shadow-lg">
                        <div className="rounded-lg bg-destructive/15 p-3 text-sm text-destructive">
                            Your MFA setup session expired or is no longer valid.
                        </div>
                        <Button type="button" className="w-full" onClick={returnToSignIn}>
                            Return to sign in
                        </Button>
                    </div>
                </div>
            ) : (
                <MfaChallengeCard
                    title="Improve security with Multifactor Authentication"
                    contextMessage={setupContextMessage}
                    availableMethods={availableMethods}
                    selectedMethod={selectedMethod}
                    onSelectMethod={(method) => {
                        setSelectedMethod(method);
                        setError('');
                        setMessage('');
                        if (method === 'TOTP' && totpSetup === null) {
                            setupTotpMutation.mutate();
                        }
                    }}
                    areMethodsDisabled={
                        statusQuery.isLoading ||
                        verifyMutation.isPending ||
                        requestCodeMutation.isPending ||
                        setupTotpMutation.isPending
                    }
                    deliveryAction={
                        selectedMethodNeedsDelivery
                            ? {
                                  label:
                                      selectedMethod === 'EMAIL'
                                          ? 'Send email code'
                                          : 'Send text message code',
                                  pendingLabel: 'Sending...',
                                  isPending: requestCodeMutation.isPending,
                                  isDisabled: verifyMutation.isPending,
                                  onClick: () => {
                                      setError('');
                                      setMessage('');
                                      requestCodeMutation.mutate(selectedMethod);
                                  },
                              }
                            : undefined
                    }
                    totpSetupAction={
                        selectedMethod === 'TOTP'
                            ? {
                                  qrCode: totpSetup?.qr_code ?? null,
                                  isPending: setupTotpMutation.isPending,
                                  isDisabled: verifyMutation.isPending,
                                  onStart: () => {
                                      setError('');
                                      setMessage('');
                                      setupTotpMutation.mutate();
                                  },
                              }
                            : undefined
                    }
                    codeInputProps={register('totp_code')}
                    codeInputDisabled={verifyMutation.isPending}
                    codeError={errors.totp_code?.message}
                    errorMessage={error}
                    successMessage={message}
                    submitLabel="Verify and continue"
                    submitPendingLabel="Verifying..."
                    isSubmitPending={verifyMutation.isPending}
                    isSubmitDisabled={
                        verifyMutation.isPending ||
                        (selectedMethod === 'TOTP' && totpSetup === null)
                    }
                    onSubmit={handleSubmit(onSubmit)}
                />
            )}
        </>
    );
}
